import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { EditorLayout } from './EditorLayout';
import { MobileEditorLayout } from './mobile/MobileEditorLayout';
import { ProjectsDashboard } from './ProjectsDashboard';
import { useProjectLoader } from '../hooks/useProjectLoader';
import useMediaQuery, { MD_BREAKPOINT } from '@/hooks/useMediaQuery';

/** Renders the projects dashboard at /studio, or the editor at /studio/:projectId */
export function EditorPage() {
  const { projectId } = useParams<{ projectId?: string }>();

  if (!projectId) {
    return <ProjectsDashboard />;
  }

  return <EditorWithProject />;
}

function EditorWithProject() {
  const { t } = useTranslation();
  const { state, error } = useProjectLoader();
  const isMobile = useMediaQuery(MD_BREAKPOINT);

  useEffect(() => {
    document.title = t('editor.pageTitle', 'VidNight Studio');
  }, [t]);

  if (state === 'loading') {
    return (
      <div className="flex items-center justify-center h-screen bg-[var(--user-page-bg)] text-[var(--user-page-text)]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-current border-t-transparent rounded-full animate-spin" />
          <p className="text-sm opacity-60">{t('editor.projectLoading', 'Loading project...')}</p>
        </div>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="flex items-center justify-center h-screen bg-[var(--user-page-bg)] text-[var(--user-page-text)]">
        <p className="text-sm text-red-400">{t(`editor.${error}`, 'Project not found')}</p>
      </div>
    );
  }

  return isMobile ? <MobileEditorLayout /> : <EditorLayout />;
}
